// notificationSearchUtils.js

export const normalizeForSearch = (s = "") =>
	String(s)
		.toLowerCase()
		.replace(/,/g, "")
		// normalize hour leading zeros for time tokens: "09:00" -> "9:00", "00:15" -> "0:15"
		.replace(/\b0+(\d{1,2})(?=:)/g, "$1")
		.replace(/\s+/g, " ")
		.trim();
export const buildDateTimeFormatters = userTimeZone => {
	const time24Formatter = new Intl.DateTimeFormat("en-GB", {
		timeZone: userTimeZone,
		hour: "2-digit",
		minute: "2-digit",
		hour12: false
	});

	const hour24Formatter = new Intl.DateTimeFormat("en-GB", {
		timeZone: userTimeZone,
		hour: "2-digit",
		hour12: false
	});

	return { time24Formatter, hour24Formatter };
};

export const buildCreatedSearchFields = ({
	createdRaw,
	dateOnlyFormatter,
	dateTimeFormatter,
	time24Formatter,
	hour24Formatter
}) => {
	let createdDateOnlyLower = "";
	let createdDateTimeLower = "";
	let createdTime24 = ""; // "22:14"
	let createdHour24 = ""; // "22"
	let createdMD = ""; // "02/03"
	let createdMDY = ""; // "02/03/2026"

	if (!createdRaw) {
		return {
			createdDateOnlyLower,
			createdDateTimeLower,
			createdTime24,
			createdHour24,
			createdMD,
			createdMDY
		};
	}

	const dt = new Date(createdRaw);

	const mm = String(dt.getMonth() + 1).padStart(2, "0");
	const dd = String(dt.getDate()).padStart(2, "0");
	const yyyy = String(dt.getFullYear());

	createdMD = `${mm}/${dd}`;
	createdMDY = `${mm}/${dd}/${yyyy}`;

	createdDateOnlyLower = normalizeForSearch(dateOnlyFormatter.format(dt));
	createdDateTimeLower = normalizeForSearch(dateTimeFormatter.format(dt));

	createdTime24 = time24Formatter.format(dt);
	createdHour24 = hour24Formatter.format(dt);

	return {
		createdDateOnlyLower,
		createdDateTimeLower,
		createdTime24,
		createdHour24,
		createdMD,
		createdMDY
	};
};

// ---- Query parsing ----
export const parseSearchQuery = (raw, { is24Hour } = {}) => {
	const q = normalizeForSearch(raw);

	// detect meridiem anywhere at end: "pm", "9pm", "9:00 pm"
	const meridiemMatch = q.match(/\b(am|pm)\b$/);

	// time-only must include ":" -> "22:", "22:1", "22:14"
	let timeOnlyMatch = q.match(/^(\d{1,2}):(\d{0,2})(?:\s*(am|pm))?$/);

	// If user is in 12-hour mode AND query has no am/pm,
	// do NOT treat it as a strict time-only query.
	// Let it fall back to general text search (old behavior).
	if (!is24Hour && timeOnlyMatch && !meridiemMatch) {
		timeOnlyMatch = null;
	}

	// date-only: "02", "02/", "02/0", "02/03", "02/03/", "02/03/2026"
	const dateOnlyMatch = q.match(/^(\d{1,2})(?:\/(\d{0,2})(?:\/(\d{0,4}))?)?$/);

	// date+time:
	// "02/03/2026 22"
	// "02/03/2026 22:"
	// "02/03/2026 22:1"
	// "02/03/2026 22:14"
	// "02/03/2026 2"
	const dateTimeMatch = q.match(/^(\d{1,2})\/(\d{1,2})(?:\/(\d{2,4}))?\s+(\d{1,2})(?::(\d{0,2}))?(?:\s*(am|pm))?$/);

	// Only normalize zeros for DATE-like queries (contain / or -) AND not time-only queries
	const shouldNormalizeDate = !timeOnlyMatch && /[\/-]/.test(q);
	const queryToUse = shouldNormalizeDate ? q.replace(/(^|[\/-])0+(\d)/g, "$1$2") : q;

	return {
		q,
		queryToUse,
		timeOnlyMatch,
		dateOnlyMatch,
		dateTimeMatch,
		meridiemMatch
	};
};

// ---- Matching helpers ----
export const matchDateTimeQuery = (dateTimeMatch, fields) => {
	if (!dateTimeMatch) return null;

	const { createdMD, createdMDY, createdTime24, createdHour24 } = fields;

	const m = dateTimeMatch[1].padStart(2, "0");
	const d = dateTimeMatch[2].padStart(2, "0");

	const yRaw = dateTimeMatch[3]; // optional
	const hRaw = dateTimeMatch[4]; // "2" or "22"
	const h = hRaw.padStart(2, "0"); // "02" or "22"
	const minRaw = dateTimeMatch[5]; // undefined | "" | "1" | "14"

	// date must match
	if (yRaw) {
		const y = yRaw.length === 2 ? `20${yRaw}` : yRaw;
		if (createdMDY !== `${m}/${d}/${y}`) return false;
	} else {
		if (createdMD !== `${m}/${d}`) return false;
	}

	// " ... 22" or " ... 22:" or " ... 2" -> hour match
	if (minRaw === undefined || minRaw === "") {
		// 1-digit hour after date => prefix match ("2" matches 20-23)
		if (hRaw.length === 1) {
			return createdHour24.startsWith(hRaw);
		}
		return createdHour24 === h;
	}

	// " ... 22:1" => prefix match (22:10..22:19)
	if (minRaw.length === 1) {
		return createdTime24.startsWith(`${h}:${minRaw}`);
	}

	// " ... 22:14" => exact match
	if (minRaw.length === 2) {
		const minNum = Number(minRaw);
		if (Number.isNaN(minNum) || minNum > 59) return false;
		return createdTime24 === `${h}:${minRaw}`;
	}

	return false;
};

export const matchDateOnlyQuery = (dateOnlyMatch, q, fields) => {
	// only run this block when user is clearly typing a date (has "/")
	if (!dateOnlyMatch || !q.includes("/")) return null;

	const { createdMD, createdMDY } = fields;

	const qM = dateOnlyMatch[1].padStart(2, "0");
	const qD = dateOnlyMatch[2]; // "" | "0" | "03" | undefined
	const qY = dateOnlyMatch[3]; // optional

	// "02/" -> month filter
	if (qD === undefined || qD === "") {
		return createdMD.startsWith(`${qM}/`);
	}

	// "02/0" -> day prefix: 02/01..02/09
	if (qD.length === 1) {
		return createdMD.startsWith(`${qM}/${qD}`);
	}

	// "02/03" -> exact day match
	const qD2 = qD.padStart(2, "0");
	if (!qY) {
		return createdMD === `${qM}/${qD2}`;
	}

	// "02/03/2" -> year prefix match
	return createdMDY.startsWith(`${qM}/${qD2}/${qY}`);
};

const to24Hour = (hour12, ampm) => {
	let h = Number(hour12);
	if (Number.isNaN(h) || h < 1 || h > 12) return null;

	if (ampm === "am") {
		if (h === 12) h = 0;
	} else if (ampm === "pm") {
		if (h !== 12) h += 12;
	}
	return String(h).padStart(2, "0");
};

export const matchTimeOnlyQuery = (timeOnlyMatch, fields) => {
	if (!timeOnlyMatch) return null;

	const { createdTime24, createdHour24 } = fields;

	const qHourRaw = timeOnlyMatch[1]; // "9" or "09" or "21"
	const qMinRaw = timeOnlyMatch[2]; // "" | "5" | "51"
	const qAmPm = timeOnlyMatch[3]; // undefined | "am" | "pm"

	// If user typed am/pm, convert hour to 24h for strict matching
	const qHour = qAmPm ? to24Hour(qHourRaw, qAmPm) : qHourRaw.padStart(2, "0");
	if (qAmPm && qHour === null) return false;

	// "07:" => hour-only
	if (qMinRaw === "") {
		return createdHour24 === qHour;
	}

	// "07:5" => prefix (07:50..07:59)
	if (qMinRaw.length === 1) {
		return createdTime24.startsWith(`${qHour}:${qMinRaw}`);
	}

	// "07:51" => exact
	if (qMinRaw.length === 2) {
		const minNum = Number(qMinRaw);
		if (Number.isNaN(minNum) || minNum > 59) return false;
		return createdTime24 === `${qHour}:${qMinRaw}`;
	}

	return false;
};

export const buildCombinedSearchText = parts => normalizeForSearch(parts.join(" "));

export const matchesSearch = ({
	searchString,
	notification,
	dateOnlyFormatter,
	dateTimeFormatter,
	time24Formatter,
	hour24Formatter,
	alertFilterSettings,
	userFeeds,
	collectAllPrimitiveValues,
	is24Hour
}) => {
	const { q, queryToUse, timeOnlyMatch, dateOnlyMatch, dateTimeMatch } = parseSearchQuery(searchString, {
		is24Hour
	});

	const fields = buildCreatedSearchFields({
		createdRaw: notification.createdDate,
		dateOnlyFormatter,
		dateTimeFormatter,
		time24Formatter,
		hour24Formatter
	});

	// 1) date + time
	const dt = matchDateTimeQuery(dateTimeMatch, fields);
	if (dt !== null) return dt;

	// 2) date only
	const d = matchDateOnlyQuery(dateOnlyMatch, q, fields);
	if (d !== null) return d;

	// 3) time only (only active in 24h OR if query includes am/pm)
	const t = matchTimeOnlyQuery(timeOnlyMatch, fields);
	if (t !== null) return t;

	// 4) general search
	const typeObj = alertFilterSettings?.types?.find(type => type.id === notification.eventTypeId);
	const eventTypeName = normalizeForSearch(typeObj?.name || "");

	const priorityObj = alertFilterSettings?.priorities?.find(p => p.id === notification.eventPriorityId);
	const priorityName = normalizeForSearch(priorityObj?.name || "");

	const objectNameLower = normalizeForSearch(notification.activity?.object?.name || "");
	const objectFilterLower = normalizeForSearch(notification.activity?.object?.filter || "");
	const objectSubFilterLower = normalizeForSearch(notification.activity?.object?.subFilter || "");

	const objectFeedId = notification.activity?.object?.feedId;
	const objectFeedNameLower = normalizeForSearch(
		objectFeedId && userFeeds[objectFeedId]?.name ? userFeeds[objectFeedId].name : ""
	);

	const targetNameLower = normalizeForSearch(notification.activity?.target?.name || "");

	const targetFeedId = notification.activity?.target?.feedId;
	const targetFeedNameLower = normalizeForSearch(
		targetFeedId && userFeeds[targetFeedId]?.name ? userFeeds[targetFeedId].name : ""
	);

	const objectMetaData = normalizeForSearch(
		collectAllPrimitiveValues(notification.activity?.object?.metadata).join(" ")
	);

	const msg = normalizeForSearch(notification.message || "");

	const combined = buildCombinedSearchText([
		msg,
		fields.createdDateOnlyLower,
		fields.createdDateTimeLower,
		eventTypeName,
		priorityName,
		objectMetaData,
		objectNameLower,
		objectFeedNameLower,
		objectFilterLower,
		objectSubFilterLower,
		targetNameLower,
		targetFeedNameLower
	]);

	return combined.includes(queryToUse);
};
